/*
 * Home.jsx — public landing page (light redesign, v1).
 *
 * Design philosophy (set 2026-05-13):
 *   - Light & airy, NOT dark/video. Mobile-first.
 *   - Two-column sections like Buffer / SocialBee, but with our differentiators
 *     leading: profession-aware AI, served-fresh idea stream, team amplification.
 *   - Grab the right professional in the first viewport: rotating profession
 *     word in the headline + infinite profession marquee directly below.
 *   - Sections fade/slide in on scroll (Reveal wrapper, IntersectionObserver).
 *   - Alternating backgrounds: white, very-pale-cyan (#f5fbff).
 *   - One screenshot per section. No autoplay video on the landing page.
 *
 * If you're tempted to add background video back — don't. The whole point of
 * this redesign was to drop it: mobile playback was unreliable and the page
 * felt heavy. Static imagery + scroll motion does the work instead.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

const PUBLIC_SITE = process.env.REACT_APP_PUBLIC_SITE_ORIGIN || 'https://ehandshake.com';

/* ─── Brand palette ─────────────────────────────────────────────────────── */
const BRAND      = '#00aef7';
const BRAND_DARK = '#0098d8';

/* ═══════════════════════════════════════════════════════════════════════════
 * SCROLL REVEAL — small IntersectionObserver hook + wrapper.
 *
 * One-shot: once an element enters the viewport it stays revealed. Disconnects
 * the observer so we don't keep firing on re-scrolls. Honors prefers-reduced-
 * motion by skipping the slide animation when the user has it set.
 * ═══════════════════════════════════════════════════════════════════════════ */
function useInView(options = { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        obs.disconnect();
      }
    }, options);
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function Reveal({ children, delay = 0, as: Tag = 'div', className = '' }) {
  const [ref, inView] = useInView();
  const style = {
    transitionDelay: `${delay}ms`,
  };
  return (
    <Tag
      ref={ref}
      style={style}
      className={`transition-all duration-700 ease-out ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } ${className}`}
    >
      {children}
    </Tag>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * PROFESSION ICONS — outline SVGs sized to fill their container.
 * Lifted from the previous Home.jsx, plus a handful of new trades so the
 * marquee shows variety. New icons use the same ICON_PROPS object so stroke
 * width and viewBox stay consistent.
 * ═══════════════════════════════════════════════════════════════════════════ */
const ICON_PROPS = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  viewBox: '0 0 24 24',
  className: 'w-full h-full',
  'aria-hidden': true,
};
const PlumberIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" className="w-full h-full" aria-hidden="true">
    <path d="M771-593 630-734l-85 84-85-84 113-114q12-12 27-17.5t30-5.5q16 0 30.5 5.5T686-848l85 85q18 17 26.5 39.5T806-678q0 23-8.5 45T771-593ZM220-409q-18-18-18-42.5t18-42.5l98-99 85 85-99 99q-17 18-41.5 18T220-409Zm-43 297q-11-12-17-26.5t-6-30.5q0-16 5.5-30.5T177-226l283-282-127-128q-18-17-18-41.5t18-42.5q17-18 42-18t43 18l127 127 57-57 112 114q12 12 12 28t-12 28q-12 12-28 12t-28-12L290-112q-12 12-26.5 17.5T234-89q-15 0-30-6t-27-17Z" />
  </svg>
);
const ElectricianIcon = () => (
  <svg {...ICON_PROPS}><path d="M13 2 4 14h6l-1 8 9-12h-6z" /></svg>
);
const HvacIcon = () => (
  <svg {...ICON_PROPS}><path d="M12 2v20M2 12h20M5 5l14 14M5 19 19 5" /></svg>
);
const RooferIcon = () => (
  <svg {...ICON_PROPS}><path d="m2 11 10-7 10 7" /><path d="M5 11v9h14v-9" /><path d="M5 14h14M5 17h14" /></svg>
);
const RealtorIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" className="w-full h-full" aria-hidden="true">
    <path d="M760-400v-260L560-800 360-660v60h-80v-100l280-200 280 200v300h-80ZM560-800Zm20 160h40v-40h-40v40Zm-80 0h40v-40h-40v40Zm80 80h40v-40h-40v40Zm-80 0h40v-40h-40v40ZM280-220l278 76 238-74q-5-9-14.5-15.5T760-240H558q-27 0-43-2t-33-8l-93-31 22-78 81 27q17 5 40 8t68 4q0-11-6.5-21T578-354l-234-86h-64v220ZM40-80v-440h304q7 0 14 1.5t13 3.5l235 87q33 12 53.5 42t20.5 66h80q50 0 85 33t35 87v40L560-60l-280-78v58H40Zm80-80h80v-280h-80v280Z" />
  </svg>
);
const MortgageIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" className="w-full h-full" aria-hidden="true">
    <path d="M200-280v-280h80v280h-80Zm240 0v-280h80v280h-80ZM80-120v-80h800v80H80Zm600-160v-280h80v280h-80ZM80-640v-80l400-200 400 200v80H80Zm178-80h444-444Zm0 0h444L480-830 258-720Z" />
  </svg>
);
const InsuranceIcon = () => (
  <svg {...ICON_PROPS}><path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3Z" /><path d="m9 12 2 2 4-4" /></svg>
);
const TaxIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <rect x="6" y="4" width="12" height="4" rx="1" />
    <circle cx="8" cy="12" r="0.5" fill="currentColor" />
    <circle cx="12" cy="12" r="0.5" fill="currentColor" />
    <circle cx="16" cy="12" r="0.5" fill="currentColor" />
    <circle cx="8" cy="16" r="0.5" fill="currentColor" />
    <circle cx="12" cy="16" r="0.5" fill="currentColor" />
    <circle cx="16" cy="16" r="0.5" fill="currentColor" />
  </svg>
);
const LandscaperIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" className="w-full h-full" aria-hidden="true">
    <path d="m46-160 138-276q10-20 28.5-32t41.5-12q24 0 44 12.5t29 35.5l27 66q2 6 9 5.5t9-6.5l86-287q14-48 53.5-77t89.5-29q49 0 87.5 28.5T742-657l173 497h-85L666-632q-8-23-25-35.5T601-680q-23 0-40.5 13T535-631l-86 287q-9 28-32.5 46T363-280q-27 0-50-14.5T280-335l-27-66-118 241H46Zm194-400q-50 0-85-35.5T120-680q0-50 35-85t85-35q50 0 85 35t35 85q0 49-35 84.5T240-560Zm0-80q17 0 28.5-11.5T280-680q0-17-11.5-28.5T240-720q-17 0-28.5 11.5T200-680q0 17 11.5 28.5T240-640Zm123 360ZM240-680Z" />
  </svg>
);
const MechanicIcon = () => (
  <svg viewBox="0 -960 960 960" fill="currentColor" className="w-full h-full" aria-hidden="true">
    <path d="M440-80v-120H160v-80h640v80H520v120h-80Zm-51.5-431.5Q400-523 400-540t-11.5-28.5Q377-580 360-580t-28.5 11.5Q320-557 320-540t11.5 28.5Q343-500 360-500t28.5-11.5Zm240 0Q640-523 640-540t-11.5-28.5Q617-580 600-580t-28.5 11.5Q560-557 560-540t11.5 28.5Q583-500 600-500t28.5-11.5ZM200-616l66-192q5-14 16.5-23t25.5-9h344q14 0 25.5 9t16.5 23l66 192v264q0 14-9 23t-23 9h-16q-14 0-23-9t-9-23v-48H280v48q0 14-9 23t-23 9h-16q-14 0-23-9t-9-23v-264Zm106-64h348l-28-80H334l-28 80Zm-26 80v120-120Zm0 120h400v-120H280v120Z" />
  </svg>
);
const LawyerIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 3v18" />
    <path d="M5 7h14" />
    <path d="M5 7 3 13a3 3 0 0 0 4 0L5 7Z" />
    <path d="M19 7l-2 6a3 3 0 0 0 4 0l-2-6Z" />
    <path d="M8 21h8" />
  </svg>
);
const DentistIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 4c-3 0-6 1-7 3-1 3 0 7 1 11 .5 2 2 2.5 3-1 .8-2.8 1.5-3 3-3s2.2.2 3 3c1 3.5 2.5 3 3 1 1-4 2-8 1-11-1-2-4-3-7-3Z" />
  </svg>
);
const TrainerIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M4 9v6M8 7v10M16 7v10M20 9v6" />
    <path d="M8 12h8" />
  </svg>
);
const PhotographerIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7l1.5-2h5L16 7" />
    <circle cx="12" cy="13.5" r="3.5" />
  </svg>
);
const ChefIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M6 14a4 4 0 1 1 1-7.9 4 4 0 0 1 10 0A4 4 0 1 1 18 14v6H6v-6Z" />
    <path d="M6 17h12" />
  </svg>
);
const StylistIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="6" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <path d="m20 4-11 14M20 20 9 6" />
  </svg>
);
const CleanerIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M8 3v6h8V3" />
    <path d="M6 9h12l-1 12H7L6 9Z" />
  </svg>
);
const PainterIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="3" y="4" width="14" height="6" rx="1" />
    <path d="M17 7h3v4H10v3" />
    <rect x="8" y="14" width="4" height="6" rx="1" />
  </svg>
);
const VetIcon = () => (
  <svg {...ICON_PROPS}>
    <circle cx="6" cy="10" r="2" />
    <circle cx="18" cy="10" r="2" />
    <circle cx="9" cy="5" r="2" />
    <circle cx="15" cy="5" r="2" />
    <path d="M12 11c-3 0-6 3-6 6 0 2 2 3 4 3h4c2 0 4-1 4-3 0-3-3-6-6-6Z" />
  </svg>
);
const HandymanIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="m13 6 5 5-9 9-5-5 9-9Z" />
    <path d="m13 6 3-3 5 5-3 3" />
  </svg>
);
const ArchitectIcon = () => (
  <svg {...ICON_PROPS}>
    <path d="M12 3v18" />
    <path d="M4 21 12 5l8 16" />
    <path d="M8 14h8" />
  </svg>
);

/* The marquee list — 20 trades. Order influences first-impression because the
 * strip starts in the visible region. Keep the most common trades up front
 * (plumber/electrician/realtor/HVAC are our heaviest signup categories). */
const PROFESSIONS = [
  { name: 'Plumbers',          icon: <PlumberIcon /> },
  { name: 'Electricians',      icon: <ElectricianIcon /> },
  { name: 'HVAC Techs',        icon: <HvacIcon /> },
  { name: 'Roofers',           icon: <RooferIcon /> },
  { name: 'Realtors',          icon: <RealtorIcon /> },
  { name: 'Mortgage Brokers',  icon: <MortgageIcon /> },
  { name: 'Insurance Agents',  icon: <InsuranceIcon /> },
  { name: 'CPAs & Tax Pros',   icon: <TaxIcon /> },
  { name: 'Landscapers',       icon: <LandscaperIcon /> },
  { name: 'Mechanics',         icon: <MechanicIcon /> },
  { name: 'Lawyers',           icon: <LawyerIcon /> },
  { name: 'Dentists',          icon: <DentistIcon /> },
  { name: 'Personal Trainers', icon: <TrainerIcon /> },
  { name: 'Photographers',     icon: <PhotographerIcon /> },
  { name: 'Caterers & Chefs',  icon: <ChefIcon /> },
  { name: 'Hair Stylists',     icon: <StylistIcon /> },
  { name: 'Cleaners',          icon: <CleanerIcon /> },
  { name: 'Painters',          icon: <PainterIcon /> },
  { name: 'Veterinarians',     icon: <VetIcon /> },
  { name: 'Handymen',          icon: <HandymanIcon /> },
  { name: 'Architects',        icon: <ArchitectIcon /> },
];

/* Generic fallback icon — used for marquee chips whose profession name
 * doesn't match any of the keyword rules below. Briefcase shape is
 * deliberately neutral so it doesn't read as "wrong" for any specific
 * trade (vs, say, picking one of our trade icons as a default). */
const GenericTradeIcon = () => (
  <svg {...ICON_PROPS}>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

/* Keyword → icon mapping for the marquee. Each rule's `keywords` list is
 * checked against the lowercased profession name (substring match); the
 * first matching rule wins. The mapping is intentionally loose so a wide
 * variety of DB names ("Plumbing Contractor", "HVAC Service Provider",
 * "Bankruptcy Law Firm", "Cosmetics-Skin Care Retailer", etc.) get a
 * recognizable icon without us having to maintain a per-canonical map.
 *
 * Anything that doesn't match falls back to GenericTradeIcon so every
 * chip in the marquee is visually anchored — no naked-text chips mixed
 * with iconed ones. Order matters here: more specific keywords come
 * before generic ones (e.g. "law" must come AFTER "plumbing" so a
 * "Plumbing Law Firm", though unlikely, still gets the plumbing icon
 * if listed before the law rule. We list law later anyway since law-
 * firm names are common and won't accidentally pull a trade icon). */
const ICON_KEYWORD_RULES = [
  { keywords: ['plumb'],                                                                icon: <PlumberIcon /> },
  { keywords: ['electric'],                                                             icon: <ElectricianIcon /> },
  { keywords: ['hvac', 'heating', 'cooling', 'air condition', 'air duct'],              icon: <HvacIcon /> },
  { keywords: ['roof'],                                                                 icon: <RooferIcon /> },
  { keywords: ['real estate', 'realtor', 'realty', 'property manag', 'home stag', 'home inspect'], icon: <RealtorIcon /> },
  { keywords: ['mortgage'],                                                             icon: <MortgageIcon /> },
  { keywords: ['insurance'],                                                            icon: <InsuranceIcon /> },
  { keywords: ['tax', 'cpa', 'accounting', 'audit', 'bookkeep', 'bank', 'forensic'],    icon: <TaxIcon /> },
  { keywords: ['landscape', 'lawn', 'tree care', 'florist', 'garden'],                  icon: <LandscaperIcon /> },
  { keywords: ['mechanic', 'auto', 'tire', 'car dealer', 'auto body', 'auto glass', 'auto detail', 'marine'], icon: <MechanicIcon /> },
  { keywords: ['lawyer', 'law firm', 'attorney', 'legal', 'patent', 'trademark', 'litigation'], icon: <LawyerIcon /> },
  { keywords: ['dent'],                                                                 icon: <DentistIcon /> },
  { keywords: ['trainer', 'fitness', 'gym', 'yoga', 'dance', 'piano', 'coach', 'nutritionist', 'physical therap', 'chiropract'], icon: <TrainerIcon /> },
  { keywords: ['photo', 'video', 'film'],                                               icon: <PhotographerIcon /> },
  { keywords: ['cater', 'chef', 'cooking', 'food', 'restaurant', 'baker', 'coffee', 'bar'], icon: <ChefIcon /> },
  { keywords: ['hair', 'salon', 'stylist', 'barber', 'nail', 'beauty', 'makeup', 'tattoo', 'spa', 'cosmetic', 'apparel', 'shop'], icon: <StylistIcon /> },
  { keywords: ['clean', 'janitor', 'laundry', 'dry clean', 'restoration', 'chimney', 'mold', 'pest', 'window'], icon: <CleanerIcon /> },
  { keywords: ['paint', 'sign', 'flooring', 'tile', 'carpet', 'fabric'],                icon: <PainterIcon /> },
  { keywords: ['vet', 'animal', 'pet', 'dog', 'cat'],                                   icon: <VetIcon /> },
  { keywords: ['handyman', 'repair', 'remodel', 'contractor', 'construct', 'gutter', 'solar', 'security', 'locksmith', 'mover', 'movin', 'relocat', 'asphalt', 'paving', 'survey', 'welding'], icon: <HandymanIcon /> },
  { keywords: ['architect', 'design', 'engineer', 'interior decor', 'art', 'craft', 'graphic'], icon: <ArchitectIcon /> },
];

function iconForProfessionName(name) {
  const lower = String(name || '').toLowerCase();
  for (const { keywords, icon } of ICON_KEYWORD_RULES) {
    if (keywords.some((k) => lower.includes(k))) return icon;
  }
  return <GenericTradeIcon />;
}

/* Fallback list for the rotating headline word — used until the live list
 * arrives from GET /api/professions/headline-trades, and as a backstop if
 * the fetch fails. The live list is sourced from profession_canonical
 * (~175 rows), normalized through the same sentence-case + pluralize +
 * 17-char length filter that the API applies, so these fallbacks already
 * match the format. Keep these entries ≤17 characters or they'll cause
 * layout shift when they cycle in. */
const FALLBACK_TRADES = [
  'Plumbers',
  'Realtors',
  'Electricians',
  'Hvac techs',
  'Roofers',
  'Cpas',
  'Lawyers',
  'Insurance agents',
  'Landscapers',
  'Photographers',
  'Personal trainers',
  'Caterers',
  'Hair stylists',
  'Dentists',
];

const HEADLINE_ROTATE_MS = 2500;

/* Hand-curated fallback affinity for the FALLBACK_TRADES list. Used until
 * the live response lands, and as a backstop if the fetch fails. Keys
 * are the lowercase trade display name (matches the API contract). Same
 * shape the server returns: { lowercaseTrade: [primary, secondary, tertiary] }.
 *
 * These don't have to be perfect — the seed script will overwrite them
 * with AI-ranked data, and admins can override individual rows from there. */
const FALLBACK_AFFINITY = {
  'plumbers':          ['tiktok', 'instagram', 'facebook'],
  'realtors':          ['tiktok', 'facebook',  'instagram'],
  'electricians':      ['tiktok', 'instagram', 'facebook'],
  'hvac techs':        ['facebook','tiktok',   'instagram'],
  'roofers':           ['facebook','instagram','tiktok'],
  'cpas':              ['linkedin','facebook', 'youtube'],
  'lawyers':           ['linkedin','facebook', 'youtube'],
  'insurance agents':  ['facebook','linkedin', 'instagram'],
  'landscapers':       ['instagram','tiktok',  'facebook'],
  'photographers':     ['instagram','tiktok',  'youtube'],
  'personal trainers': ['instagram','tiktok',  'youtube'],
  'caterers':          ['instagram','tiktok',  'facebook'],
  'hair stylists':     ['instagram','tiktok',  'facebook'],
  'dentists':          ['instagram','facebook','tiktok'],
};

/* Fetch the live trade list + platform affinity from the server.
 * Returns { trades, affinity } with safe fallbacks so the caller can
 * always count on getting non-empty data.
 *
 * Lives outside the component so it can be unit-tested independently
 * and doesn't re-create on every render. */
async function fetchHeadlineTrades() {
  try {
    const res = await fetch('/api/professions/headline-trades', {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return { trades: FALLBACK_TRADES, affinity: FALLBACK_AFFINITY, allNames: [] };
    const data = await res.json();
    const trades = Array.isArray(data?.trades) ? data.trades.filter(Boolean) : [];
    const affinity = (data?.affinity && typeof data.affinity === 'object') ? data.affinity : {};
    const allNames = Array.isArray(data?.allNames) ? data.allNames.filter(Boolean) : [];
    return {
      trades:   trades.length ? trades : FALLBACK_TRADES,
      affinity: trades.length ? affinity : FALLBACK_AFFINITY,
      allNames, // can be empty — the marquee falls back to the hardcoded
                // PROFESSIONS list when this is empty (first paint / no data)
    };
  } catch (_) {
    return { trades: FALLBACK_TRADES, affinity: FALLBACK_AFFINITY, allNames: [] };
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
 * PLATFORM BADGES — colored brand chips for the platforms a profession
 * cares about.
 *
 * Two tiers:
 *   - PLATFORM_META: the 7 platforms we currently publish to. Rendered as
 *     full-color brand-pill badges. Adding a new integration here makes
 *     it light up everywhere instantly — no DB changes needed.
 *   - UPCOMING_PLATFORM_META: 7 platforms our seed AI may recommend for a
 *     trade but we don't publish to yet (pinterest, threads, snapchat,
 *     reddit, bluesky, nextdoor, shopify). Rendered as muted outline
 *     pills with a "soon" treatment. Visitor sees them as "platforms
 *     your trade should be on; not in eHandshake yet."
 *
 * The whole strip morphs in sync with the rotating trade word — the
 * visitor reads "AI Marketing for {Interior decorators}" and sees, right
 * below, "Where Interior decorators post: [Instagram] [TikTok] [Facebook]
 * [YouTube]   • also active on: Pinterest".
 *
 * Brand colors come from each platform's official media kit. Icons are
 * inline SVG so we don't ship a sprite sheet for 14 logos.
 * ═══════════════════════════════════════════════════════════════════════════ */
const PLATFORM_META = {
  facebook:  { label: 'Facebook',  bg: '#1877F2', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.99 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.99 22 12z" />
    </svg>
  )},
  instagram: { label: 'Instagram', bg: 'linear-gradient(135deg,#f9ce34 0%,#ee2a7b 50%,#6228d7 100%)', icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  )},
  tiktok:    { label: 'TikTok',    bg: '#000000', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.07A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.49a8.13 8.13 0 0 0 4.77 1.52V6.55a4.85 4.85 0 0 1-1.84-.06z" />
    </svg>
  )},
  youtube:   { label: 'YouTube',   bg: '#FF0000', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )},
  linkedin:  { label: 'LinkedIn',  bg: '#0A66C2', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )},
  x:         { label: 'X',         bg: '#000000', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )},
  whatsapp:  { label: 'WhatsApp',  bg: '#25D366', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.554-5.338 11.89-11.893 11.89a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26L3.515 19l4.04-1.06zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
    </svg>
  )},
};

/* Platforms our seed AI may rank for a trade but eHandshake doesn't
 * publish to YET. Rendered with full brand colors — same visual
 * treatment as supported platforms above. The "Coming soon:" label
 * preceding them in the render is what tells the user these aren't
 * available yet; the brand pill itself stays proudly on-brand.
 * When we add a platform to PLATFORM_META above, remove its entry here.
 *
 * SVG paths sourced from simple-icons (MIT licensed). bg colors taken
 * from each platform's official media kit.
 */
const UPCOMING_PLATFORM_META = {
  pinterest: { label: 'Pinterest', bg: '#E60023', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.748-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z" />
    </svg>
  )},
  threads: { label: 'Threads', bg: '#000000', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M17.751 11.111c-.072-.034-.146-.067-.221-.099-.13-2.401-1.441-3.777-3.642-3.791h-.029c-1.318 0-2.413.562-3.088 1.586l1.215.832c.504-.762 1.293-.924 1.873-.924h.02c.722.005 1.266.215 1.617.625.255.299.426.713.508 1.234-.622-.106-1.295-.139-2.014-.097-2.027.117-3.331 1.299-3.244 2.943.045.834.46 1.55 1.171 2.018.602.397 1.378.59 2.184.546 1.064-.058 1.898-.464 2.481-1.205.443-.563.723-1.292.851-2.211.524.317.913.733 1.127 1.234.367.852.388 2.252-.755 3.394-1 1.001-2.203 1.434-4.024 1.447-2.022-.015-3.551-.663-4.543-1.928-.929-1.184-1.41-2.895-1.428-5.086.018-2.19.499-3.901 1.428-5.086.992-1.265 2.521-1.913 4.543-1.928 2.038.015 3.591.665 4.62 1.935.504.622.882 1.405 1.128 2.301l1.467-.391c-.299-1.102-.77-2.05-1.412-2.842-1.317-1.621-3.247-2.453-5.733-2.469h-.01c-2.481.016-4.388.851-5.671 2.474-1.143 1.443-1.732 3.451-1.751 5.952v.025c.019 2.5.608 4.509 1.751 5.952 1.284 1.624 3.191 2.458 5.671 2.474h.01c2.205-.015 3.759-.594 5.04-1.875 1.677-1.674 1.626-3.772 1.074-5.063-.398-.926-1.151-1.679-2.176-2.182zm-3.351 4.054c-.892.05-1.819-.35-1.865-1.215-.034-.641.456-1.357 1.92-1.441.168-.01.333-.014.495-.014.531 0 1.027.052 1.479.151-.169 2.105-1.158 2.472-2.029 2.519z" />
    </svg>
  )},
  snapchat: { label: 'Snapchat', bg: '#FFFC00', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M12.166.388c.36.024.72.044 1.07.131 1.07.27 1.94.97 2.43 1.97.39.79.41 1.65.41 2.51 0 .58-.03 1.16-.06 1.74.51-.13 1.02-.32 1.46-.65.45-.34.97-.46 1.51-.4.49.06.93.43 1.01.92.07.42-.18.84-.6 1-.74.29-1.48.58-2.18 1.01-.32.2-.51.53-.63.89-.05.13.04.27.13.36.42.46.96.84 1.45 1.21.32.24.66.46.92.78.27.32.27.83 0 1.15-.26.32-.62.49-1.01.59-.55.14-1.11.27-1.66.41-.05.04-.07.1-.08.16-.05.27-.1.55-.19.81-.03.1-.16.16-.27.13-.36-.05-.71-.13-1.07-.18-.61-.09-1.21.05-1.76.31-.61.31-1.17.7-1.77 1.03-.42.22-.87.35-1.35.41-.45.05-.91-.04-1.34-.19-.81-.31-1.55-.78-2.29-1.22-.55-.26-1.15-.4-1.76-.31-.36.05-.71.13-1.07.18-.11.03-.24-.03-.27-.13-.09-.26-.14-.54-.19-.81-.01-.06-.03-.12-.08-.16-.55-.14-1.11-.27-1.66-.41-.39-.1-.75-.27-1.01-.59-.27-.32-.27-.83 0-1.15.26-.32.6-.54.92-.78.49-.37 1.03-.75 1.45-1.21.09-.09.18-.23.13-.36-.12-.36-.31-.69-.63-.89-.7-.43-1.44-.72-2.18-1.01-.42-.16-.67-.58-.6-1 .08-.49.52-.86 1.01-.92.54-.06 1.06.06 1.51.4.44.33.95.52 1.46.65-.03-.58-.06-1.16-.06-1.74 0-.86.02-1.72.41-2.51.49-1 1.36-1.7 2.43-1.97.35-.087.71-.107 1.07-.131.36-.027.72-.018 1.08-.018.36 0 .72-.009 1.08.018z" />
    </svg>
  )},
  reddit: { label: 'Reddit', bg: '#FF4500', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.04 1.605a3.32 3.32 0 0 1 .046.568c0 2.911-3.336 5.268-7.434 5.268-4.098 0-7.434-2.357-7.434-5.268 0-.197.015-.388.045-.578-.59-.275-1.014-.882-1.014-1.586 0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12c-.689 0-1.25.561-1.25 1.25 0 .686.561 1.247 1.25 1.247.688 0 1.249-.561 1.249-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .686.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
    </svg>
  )},
  bluesky: { label: 'Bluesky', bg: '#0085FF', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" />
    </svg>
  )},
  nextdoor: { label: 'Nextdoor', bg: '#00A656', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M16.355 8.708c-2.16 0-3.91 1.681-3.91 3.755v6.51h-3.166v-6.51c0-2.075-1.75-3.755-3.91-3.755h-1.79v6.51c0 4.317 3.624 7.817 8.092 7.817 4.467 0 8.091-3.5 8.091-7.817v-6.51zM12 0C6.715 0 2.43 4.142 2.43 9.252v.733h1.79c.55 0 1.082.066 1.59.19V9.252C5.81 5.961 8.59 3.282 12 3.282s6.19 2.679 6.19 5.97v.923c.508-.124 1.04-.19 1.59-.19h1.79v-.733C21.57 4.142 17.285 0 12 0Z" />
    </svg>
  )},
  shopify: { label: 'Shopify', bg: '#96BF47', icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
      <path d="M15.337 23.979s7.216-1.561 7.216-1.595c0-.034-1.927-13.396-1.927-13.396-.033-.34-.34-.508-.578-.508s-3.318-.236-3.318-.236l-2.371-2.371c-.236-.272-.749-.18-.913-.135-.04 0-.92.272-2.371.728-1.452-4.193-3.989-4.024-3.989-4.024-1.282-.034-3.183.815-3.486 1.214-.18.135-.236.27-.236.405l-.236 23.43 11.21 1.564c0 .033 1.998-4.04 1.999-4.04l-.001-.036zm-5.21-19.347c.236-.034.405-.034.578-.034l-.578 2.135c-.847.236-1.785.508-2.71.781.508-1.451 1.452-2.61 2.71-2.882zm.272 3.825-.034-.034.034.034zm-1.213-3.96c-.578.067-1.214.236-1.792.473-.236.135-.473.236-.682.405-.272.236-.508.474-.715.781-.34.474-.578 1.045-.781 1.722.847-.272 1.722-.508 2.524-.747.135-.881.34-1.696.711-2.439a.236.236 0 0 1 .135-.169c.18-.034.34-.034.508-.034.034 0 .034 0 .92.008zm5.5 1.114c-.272 0-.578.034-.815.067l-.272-2.71v-.034c.847.236 1.45.957 1.722 2.677-.135.034-.34.034-.635 0z" />
    </svg>
  )},
};

function PlatformAffinityStrip({ trade, platforms }) {
  // Walk the ranking once, split into two buckets while preserving order:
  //   supported   → full brand-color pills
  //   upcoming    → muted outline pills with a "soon" treatment
  // Any platform name that's neither (defensive — shouldn't happen with
  // the seed script's allow-list) is silently skipped.
  const supported = [];
  const upcoming  = [];
  for (const p of (platforms || [])) {
    if (PLATFORM_META[p])             supported.push(p);
    else if (UPCOMING_PLATFORM_META[p]) upcoming.push(p);
  }

  // Re-key on the current trade so React replays the fade animation
  // when the rotating word changes. If both buckets are empty (trade
  // has no affinity data at all), render an invisible spacer of the
  // same height so the layout doesn't jump between trades.
  if (supported.length === 0 && upcoming.length === 0) {
    return <div className="mt-3 h-7" aria-hidden="true" />;
  }

  return (
    <div
      key={trade}
      className="mt-3 flex flex-wrap items-center gap-2 animate-[heroSwap_700ms_ease-out]"
      aria-live="polite"
    >
      <span className="text-[11px] md:text-xs font-medium text-gray-500 tracking-wide">
        Where <span className="text-gray-700 font-semibold">{trade}</span> post:
      </span>

      {/* Supported platforms — icon-only colored circles, no cap on count.
          Platform name surfaces on hover (desktop) / long-press (mobile)
          via title attribute. Going icon-only because the previous pill
          form (icon + name) was wide enough to wrap onto a second line
          for trades with 4-5 platforms, pushing the subheadline below
          downward mid-rotation. Pure circles stay on one line. */}
      {supported.map((p) => {
        const meta = PLATFORM_META[p];
        return (
          <span
            key={p}
            className="inline-flex items-center justify-center w-7 h-7 rounded-full text-white shadow-sm ring-1 ring-black/5"
            style={{ background: meta.bg }}
            title={meta.label}
            aria-label={meta.label}
          >
            {meta.icon}
          </span>
        );
      })}

      {/* Upcoming platforms — full brand-color treatment identical to the
          supported pills above. The "Coming soon:" label preceding them
          is what tells the visitor these aren't shipped yet; the pills
          themselves stay proudly on-brand and instantly recognizable.
          Snapchat's bright yellow is too light for a white icon, so it
          gets a dark icon via the override `text` field (default: white). */}
      {upcoming.length > 0 && (
        <>
          <span className="text-[10px] md:text-[11px] text-gray-500 italic ml-1">Coming soon:</span>
          {upcoming.map((p) => {
            const meta = UPCOMING_PLATFORM_META[p];
            // Snapchat is the only upcoming brand with a light-yellow
            // background where white-on-bg has insufficient contrast.
            // Other upcoming pills inherit text-white from the className.
            const iconColor = p === 'snapchat' ? '#000000' : '#ffffff';
            return (
              <span
                key={p}
                className="inline-flex items-center justify-center w-7 h-7 rounded-full shadow-sm ring-1 ring-black/5"
                style={{ background: meta.bg, color: iconColor }}
                title={`${meta.label} — integration coming`}
                aria-label={`${meta.label} (coming soon)`}
              >
                {meta.icon}
              </span>
            );
          })}
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * HERO — light, two-column.
 *
 * Left column: rotating profession word in the headline, subhead, two CTAs.
 * Right column: single static product screenshot.
 *
 * The rotating word uses a fixed-width inline-block so the headline doesn't
 * reflow as trades cycle. paused while the user hovers it so they can read.
 * ═══════════════════════════════════════════════════════════════════════════ */
function LightHero() {
  // Trades + affinity hydrate on mount from the public profession API.
  // Until the response lands we use the curated fallbacks so the
  // rotation + badge strip never flash empty on first paint.
  const [trades,   setTrades]   = useState(FALLBACK_TRADES);
  const [affinity, setAffinity] = useState(FALLBACK_AFFINITY);
  const [tradeIdx, setTradeIdx] = useState(0);
  const [paused,   setPaused]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchHeadlineTrades().then((live) => {
      if (cancelled) return;
      setTrades(live.trades);
      setAffinity(live.affinity);
      // Reset the rotation index so the next tick lands on a valid slot,
      // even if the live array is shorter than the fallback.
      setTradeIdx(0);
    });
    return () => { cancelled = true; };
  }, []);

  // tick the index, but only when not paused
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setTradeIdx((i) => (i + 1) % trades.length);
    }, HEADLINE_ROTATE_MS);
    return () => clearInterval(id);
  }, [paused, trades.length]);

  // Current trade word + the platforms most associated with it. Looking
  // up affinity by lowercase trade name keeps the API contract clean
  // (server emits lowercase keys) regardless of the trade's display case.
  const currentTrade = trades[tradeIdx] || trades[0];
  const currentPlatforms = affinity[(currentTrade || '').toLowerCase()] || [];

  // Hero video — JS-controlled loop with a 5-second pause on the last
  // frame between cycles. The native `loop` attribute restarts instantly
  // with no breathing room, which feels frenetic for a 22s product demo.
  // We listen for the `ended` event, hold for 5s (browser keeps the last
  // frame on screen during the pause), then rewind + play. Ref + cleanup
  // so the timeout never fires after unmount.
  const heroVideoRef = useRef(null);
  const heroVideoLoopTimeoutRef = useRef(null);
  useEffect(() => () => {
    if (heroVideoLoopTimeoutRef.current) clearTimeout(heroVideoLoopTimeoutRef.current);
  }, []);
  const handleHeroVideoEnded = () => {
    heroVideoLoopTimeoutRef.current = setTimeout(() => {
      const v = heroVideoRef.current;
      if (!v) return;
      v.currentTime = 0;
      v.play().catch(() => {});
    }, 5000);
  };

  return (
    <section className="relative bg-gradient-to-b from-white via-[#f5fbff] to-white overflow-hidden">
      {/* Faint brand-color blob top-right for warmth without being heavy.
          Pure CSS, no image — keeps the section feather-light. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-25 blur-3xl"
        style={{ background: 'radial-gradient(circle, #00aef7 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto w-[92%] max-w-7xl pt-14 pb-10 md:pt-6 md:pb-20">
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">

          {/* ─── LEFT: copy + CTA ──────────────────────────────────────── */}
          <div>
            <span
              className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] mb-4"
              style={{ color: BRAND }}
            >
              Built for local, small business professionals
            </span>

            <h1 className="text-[2.4rem] sm:text-5xl md:text-[3.4rem] lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
              AI Marketing for{' '}
              <span
                className="inline-block relative align-baseline"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                onFocus={() => setPaused(true)}
                onBlur={() => setPaused(false)}
                tabIndex={0}
                aria-live="polite"
              >
                {/* Each rotation re-keys this span so React replays the fade.
                    Underline + brand color makes it read as the "fill in the
                    blank" word — the visitor mentally swaps in their own. */}
                <span
                  key={tradeIdx}
                  className="inline-block animate-[heroSwap_700ms_ease-out] underline decoration-[3px] underline-offset-4 md:underline-offset-8"
                  style={{ color: BRAND, textDecorationColor: BRAND }}
                >
                  {trades[tradeIdx] || trades[0]}
                </span>
              </span>
              <br className="hidden md:block" /> Done for you.
            </h1>

            {/* Platform-affinity strip — morphs in sync with the rotating
                trade word. "Where {plumbers} post: [TikTok] [Instagram]
                [Facebook]" — implicit signal that we know the visitor's
                trade AND know where their peers actually live. */}
            <PlatformAffinityStrip trade={currentTrade} platforms={currentPlatforms} />

            <p className="mt-5 md:mt-6 text-gray-600 text-base md:text-lg leading-relaxed max-w-xl">
              AI that understands your industry and creates marketing ideas, by profession, ready to use.
              A local team that helps you spread the word. Everything you need to
              show up on social — without having to become an expert in marketing.
            </p>

            <div className="mt-7 md:mt-8 flex flex-wrap items-center gap-3">
              <a
                href="/?modal=signup"
                className="inline-flex items-center justify-center px-6 py-3 rounded-full text-white text-sm md:text-base font-semibold shadow-lg shadow-[#00aef7]/25 transition-transform hover:scale-[1.02]"
                style={{ background: BRAND }}
              >
                Start free →
              </a>
              <a
                href={`${PUBLIC_SITE}/search`}
                className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm md:text-base font-semibold text-gray-700 hover:text-gray-900 border border-gray-300 hover:border-gray-400 transition-colors bg-white"
              >
                See the directory
              </a>
            </div>

            <p className="mt-4 text-xs text-gray-500">
              No card required. Free tier is permanent — not a trial.
            </p>
          </div>

          {/* ─── RIGHT: hero demo video.
              22-second product walkthrough, hosted on S3 + CloudFront at
              /videos/hero/HeroVideo.mp4. Autoplay + muted + loop +
              playsInline = works on iOS / Android / desktop without the
              user having to click. preload="metadata" avoids pulling the
              full file until the element is in view (the lazy="off"
              equivalent isn't reliable for <video>, so metadata-only is
              the best we can do without an IntersectionObserver gate).
              Different in spirit from the dark BACKGROUND video the
              redesign dropped — this is a contained product demo, not
              chrome behind text. ────────────────────────────────────── */}
          {/* The right column wraps both the video AND the 4-stat strip
              underneath. Putting the stats here (rather than full-width
              below the grid) keeps them visually grouped with the video
              and avoids the awkward wide-spacing the spread-out layout
              produced at full width. */}
          <div>
            <Reveal delay={150}>
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="absolute inset-0 -translate-x-3 translate-y-3 rounded-3xl"
                  style={{ background: BRAND, opacity: 0.12 }}
                />
                <div className="relative rounded-3xl overflow-hidden bg-white shadow-[0_30px_60px_-20px_rgba(0,0,0,0.18)] ring-1 ring-gray-200/70">
                  <video
                    ref={heroVideoRef}
                    src="https://ehandshake.com/videos/hero/HeroVideo.mp4"
                    autoPlay
                    muted
                    playsInline
                    preload="metadata"
                    // Native `loop` removed — replaced with a JS handler that
                    // pauses 5s on the last frame before rewinding (see
                    // handleHeroVideoEnded above). The last decoded frame
                    // stays on screen during the pause naturally; no extra
                    // styling needed.
                    onEnded={handleHeroVideoEnded}
                    // Hide the native controls — the loop runs ambient, no
                    // pause/scrub UI. Right-click context menu is suppressed
                    // so the video can't be casually downloaded.
                    controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
                    disablePictureInPicture
                    disableRemotePlayback
                    onContextMenu={(e) => e.preventDefault()}
                    aria-label="eHandshake Design Center — short product walkthrough showing AI-suggested headline, caption, hashtags, and stock imagery being assembled and published to seven platforms."
                    className="block w-full h-auto"
                  />
                </div>
              </div>
            </Reveal>

            {/* Stats strip — directly under the video. Tight 4-col grid so
                it sits in the same visual column as the video frame
                without spreading across the whole hero. No fake user
                counts — only honest facts about the product itself. */}
            <Reveal delay={250}>
              <div className="mt-6 md:mt-8 grid grid-cols-2 md:grid-cols-4 gap-x-3 md:gap-x-4 gap-y-5 text-center">
                <Stat value="7"                    label="Social Platforms, 8 post Categories"  sub="FB · IG · X · LinkedIn · TikTok · YouTube · WhatsApp" />
                <Stat value="3.2M"                 label="Royalty-free images & video" sub="Organized around your profession and industry first... all free" />
                <Stat value={<StatInfinityIcon />} label="New Post ideas, on demand"       sub="Infinite AI ideas + crowd-sourced ideas from your profession peers" />
                <Stat value={<StatEmailIcon />}    label="Built-in Email, Land in their inbox"         sub="Send Creations as well as profile, company & team information" />
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Keyframes inlined so this file is self-contained. */}
      <style>{`
        @keyframes heroSwap {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}

/* SVG glyphs used as Stat "values" alongside the numeric/text ones (7,
 * 3.2M). Anchored at the top of their bounding box (unlike Unicode ∞
 * which renders vertically centered in its em box and threw the row's
 * alignment off when we tried to scale it up). Both sized at h-7 /
 * md:h-10 — roughly 1.5× the visible glyph height of the numeric values,
 * which optically balances the lighter visual mass of icon shapes vs
 * dense digits. */
const StatInfinityIcon = () => (
  <svg
    viewBox="0 0 64 32"
    className="inline-block w-14 h-7 md:w-20 md:h-10 align-top"
    fill="currentColor"
    aria-label="infinite"
    role="img"
  >
    <path d="M16 4C9.4 4 4 10.3 4 16s5.4 12 12 12c4.1 0 7-2.6 11-7l1-1 1 1c4 4.4 6.9 7 11 7 6.6 0 12-6.3 12-12S46.6 4 40 4c-4.1 0-7 2.6-11 7l-1 1-1-1c-4-4.4-6.9-7-11-7zm0 5c2.1 0 3.7.9 7.4 5l2 2-2 2c-3.7 4.1-5.3 5-7.4 5-3.8 0-7-3-7-7s3.2-7 7-7zm24 0c3.8 0 7 3 7 7s-3.2 7-7 7c-2.1 0-3.7-.9-7.4-5l-2-2 2-2c3.7-4.1 5.3-5 7.4-5z" />
  </svg>
);

const StatEmailIcon = () => (
  <svg
    viewBox="0 0 48 32"
    className="inline-block w-8 h-7 md:w-16 md:h-10 align-top"
    fill="currentColor"
    aria-label="email"
    role="img"
  >
    {/* Envelope: rounded outer rect + V-shaped flap line. Filled style
        matches the solid weight of the infinity glyph next to it. */}
    <path d="M4 4h40c2.2 0 4 1.8 4 4v16c0 2.2-1.8 4-4 4H4c-2.2 0-4-1.8-4-4V8c0-2.2 1.8-4 4-4zm0 4v.6l20 11.4 20-11.4V8H4zm0 4.4V24h40V12.4L25 23.3a2 2 0 0 1-2 0L4 12.4z" />
  </svg>
);

function Stat({ value, label, sub }) {
  // `value` may be a string ("7", "3.2M", "Free") or a React element
  // (icon component). Both render in the same colored block. Icons get
  // sized/positioned by their own SVG className, not by the wrapper.
  return (
    <div>
      <div className="text-3xl md:text-4xl font-extrabold tracking-tight leading-none" style={{ color: BRAND }}>
        {value}
      </div>
      <div className="mt-1 text-sm font-semibold text-gray-900">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-gray-500 leading-snug">{sub}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * PROFESSION MARQUEE — infinite horizontal scroll of trade chips.
 *
 * Two reasons this exists:
 *   1. Show breadth — visitors see their own trade represented within seconds.
 *   2. Show specificity — these aren't logos of customers (we don't have them
 *      yet), they're the kinds of businesses eHandshake is built for.
 *
 * Implementation: render the list twice in a row, then translate the row
 * left by 50% of its width on a continuous loop. Hovering the strip pauses
 * the animation so people can read.
 * ═══════════════════════════════════════════════════════════════════════════ */
function ProfessionMarquee() {
  // Two-copy trick: the duplicated chips make the seam invisible because the
  // second copy slides into the first copy's original position right as the
  // first copy slides off screen.
  //
  // Data source: same /api/professions/headline-trades endpoint as the hero
  // headline. `allNames` is the unfiltered list (~173 trades, no length
  // cap, no affinity gate). Until that fetch resolves we fall back to the
  // hardcoded PROFESSIONS array so first paint isn't empty.
  const [chips, setChips] = useState(PROFESSIONS);

  useEffect(() => {
    let cancelled = false;
    fetchHeadlineTrades().then(({ allNames }) => {
      if (cancelled) return;
      if (!Array.isArray(allNames) || allNames.length === 0) return;
      // Map each name to a {name, icon} pair via the keyword lookup.
      // Trades without a keyword match get a generic briefcase icon so
      // every chip stays visually anchored.
      const mapped = allNames.map((name) => ({
        name,
        icon: iconForProfessionName(name),
      }));
      setChips(mapped);
    });
    return () => { cancelled = true; };
  }, []);

  // Marquee speed: keep the visual pixels-per-second roughly constant
  // regardless of how many chips are in the list. With ~21 hardcoded
  // chips the loop ran at 45s. With ~173 chips that same 45s feels
  // frantic — items whiz by unreadably. Scale linearly: ~2.1s per item
  // gives a comfortable reading pace. Capped at 480s so an exploding
  // taxonomy doesn't grind the loop to a crawl either.
  const animationDuration = Math.min(480, Math.max(30, Math.round(chips.length * 2.1)));

  return (
    <section className="bg-white border-y border-gray-100">
      <div className="mx-auto w-[92%] max-w-7xl py-8 md:py-10">
        <Reveal>
          <div className="text-center mb-5">
            <span
              className="inline-block text-[11px] font-bold uppercase tracking-[0.18em]"
              style={{ color: BRAND }}
            >
              Built for professions like yours
            </span>
            <h2 className="mt-1 text-sm md:text-base text-gray-600">
              See your work in here? You&rsquo;re the reason we built this.
            </h2>
          </div>
        </Reveal>

        {/* Edge fade masks so chips dissolve at the borders instead of cutting.
            The maskImage CSS prop is supported everywhere we care about. */}
        <div
          className="relative overflow-hidden"
          style={{
            maskImage:
              'linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)',
          }}
        >
          <div
            className="flex gap-3 md:gap-4 marquee-track hover:[animation-play-state:paused] focus-within:[animation-play-state:paused]"
            style={{ animationDuration: `${animationDuration}s` }}
          >
            {/* List rendered twice. aria-hidden on the dup so screen readers
                only see the trades once. */}
            {[0, 1].map((copy) => (
              <ul
                key={copy}
                aria-hidden={copy === 1 || undefined}
                className="flex gap-3 md:gap-4 shrink-0"
              >
                {chips.map((p) => (
                  <li
                    key={`${copy}-${p.name}`}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700 text-sm font-medium whitespace-nowrap hover:bg-[#00aef7]/5 hover:border-[#00aef7]/30 hover:text-gray-900 transition-colors"
                  >
                    <span className="w-4 h-4 shrink-0" style={{ color: BRAND }}>{p.icon}</span>
                    {p.name}
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>

        <p className="mt-5 text-center text-xs text-gray-500">
          …and many more.
          <span className="mx-1.5 text-gray-300">•</span>
          The signup AI figures out exactly what you do.
        </p>
      </div>

      <style>{`
        @keyframes marqueeSlide {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-track {
          animation: marqueeSlide 45s linear infinite;
          width: max-content;
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none; }
        }
      `}</style>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Floating scroll-to-top button.
 * ═══════════════════════════════════════════════════════════════════════════ */
function ScrollTopButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const handleClick = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Scroll to top"
      title="Back to top"
      className={`fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white transition-all duration-300 ${
        visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none translate-y-2'
      }`}
      style={{ background: BRAND }}
      onMouseEnter={(e) => (e.currentTarget.style.background = BRAND_DARK)}
      onMouseLeave={(e) => (e.currentTarget.style.background = BRAND)}
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="18 15 12 9 6 15" />
      </svg>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * SCROLL-TRIGGERED LOOPING VIDEO — plays the first time it scrolls into
 * view, then pauses 5 seconds on the last frame between cycles. Same
 * loop-with-breathing-room behavior as the hero video (see
 * handleHeroVideoEnded in LightHero) — the difference is that THIS
 * video doesn't autoplay on mount. It waits until the user reaches the
 * section, which keeps below-the-fold bandwidth low and makes the
 * start feel intentional rather than ambient.
 *
 * Uses the existing one-shot useInView (which disconnects its observer
 * after first intersection) — once playback has started, scroll-away
 * doesn't pause it. The 5s pause + rewind happens via the same onEnded
 * → setTimeout pattern as the hero.
 * ═══════════════════════════════════════════════════════════════════════════ */
function LoopingDemoVideo({ src, alt }) {
  const videoRef = useRef(null);
  const loopTimeoutRef = useRef(null);
  const [scrollRef, inView] = useInView({ threshold: 0.3 });

  // First time inView flips true, start playback. One-shot hook means
  // this runs once per mount.
  useEffect(() => {
    if (!inView) return;
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {});
  }, [inView]);

  // Clear the rewind timeout on unmount so we don't try to call play()
  // on a torn-down element.
  useEffect(() => () => {
    if (loopTimeoutRef.current) clearTimeout(loopTimeoutRef.current);
  }, []);

  // 5-second hold on the last frame between loops. Browsers keep the
  // last decoded frame on screen during the timeout, so visually it
  // reads as a freeze + reset rather than a black gap.
  const handleEnded = () => {
    loopTimeoutRef.current = setTimeout(() => {
      const v = videoRef.current;
      if (!v) return;
      v.currentTime = 0;
      v.play().catch(() => {});
    }, 5000);
  };

  return (
    <div ref={scrollRef} className="relative">
      <div
        aria-hidden="true"
        className="absolute inset-0 translate-x-3 translate-y-3 rounded-3xl"
        style={{ background: BRAND, opacity: 0.10 }}
      />
      <div className="relative rounded-3xl overflow-hidden bg-white shadow-[0_24px_50px_-20px_rgba(0,0,0,0.18)] ring-1 ring-gray-200/70">
        <video
          ref={videoRef}
          src={src}
          muted
          playsInline
          preload="metadata"
          onEnded={handleEnded}
          // Hide native controls — ambient loop, no scrub UI. Right-
          // click context menu suppressed so the video can't be
          // casually downloaded.
          controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
          disablePictureInPicture
          disableRemotePlayback
          onContextMenu={(e) => e.preventDefault()}
          aria-label={alt}
          className="block w-full h-auto"
        />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * SECTION 2 — TUNED TO YOUR TRADE
 * The unique-to-us hook. AI classifies the user during signup and the whole
 * product reshapes around that profession.
 * ═══════════════════════════════════════════════════════════════════════════ */
function TunedToYourTrade() {
  return (
    <section className="bg-white">
      <div className="mx-auto w-[92%] max-w-7xl py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <Reveal>
            <div>
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: BRAND }}>
                01 · Tuned to your Profession and Industry
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight">
                We figure out what you do.<br />
                <span style={{ color: BRAND }}>Then everything reshapes around it.</span>
              </h2>
              <p className="mt-5 text-gray-600 text-base md:text-lg leading-relaxed">
                On signup, the AI reads your business website and assigns a profession to you automatically.
                Plumber. Realtor. Dentist. Lawyer. From that moment on, every
                idea, every stock photo, every caption suggestion is filtered
                through your profession or industry lens.
              </p>
              <ul className="mt-7 space-y-3">
                <BulletRow text="Eight idea categories — how-tos, stories, problems & solutions, community, humor, and more — covering every angle of why customers follow a local business." />
                <BulletRow text="Each category fills with post ideas the AI wrote for your line of work. Pick one, click it, and the draft is half-built. Or create a brand new idea from scratch, even nudging the AI in the general direction you want to go." />
                <BulletRow text="No other social tool does this. Buffer, Hootsuite, Later, SocialBee all open the same blank page and wish you luck. eHandshake gives you the ideas and then helps you refine them for your specific style." />
              </ul>
            </div>
          </Reveal>

          <Reveal delay={150}>
            {/* max-w-[80%] mx-auto shrinks both assets to ~80% of the
                column width and centers them — the user wanted them
                about 20% smaller than the previous full-column size. */}
            <div className="space-y-6 md:space-y-8 max-w-[80%] mx-auto">
              <ScreenshotFrame
                src="/images/home/ContentCategories.JPG"
                alt="The 8 post-idea categories — How Things Work, How-To & Tips, Problems & Solutions, Advice & Insights, Specials & Discounts, Stories & Experiences, Community Relations, and Humor & Personality — laid out as colored cards in the Design Center."
              />
              <LoopingDemoVideo
                src="https://ehandshake.com/videos/designcenter/8categories.mp4"
                alt="33-second walkthrough showing the 8 post-idea categories in the Design Center, each populated with AI-generated content suggestions matched to the user's profession."
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * SECTION 3 — IDEAS SERVED FRESH
 * The "we give you the ideas" beat. Inspirations strip screenshot.
 * ═══════════════════════════════════════════════════════════════════════════ */
function IdeasServedFresh() {
  return (
    <section className="bg-[#f5fbff] border-y border-[#e0f0fb]">
      <div className="mx-auto w-[92%] max-w-7xl py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Screenshot on left this time — alternating layout */}
          <Reveal>
            <ScreenshotFrame
              src="/images/home/need-inspiration.png"
              alt='The "Need Inspiration?" strip inside the Design Center — a row of profession-specific post ideas with "Use This Idea" buttons and a "Generate New Idea" tile.'
              caption="Open the app, pick an idea, the post is half-built."
            />
          </Reveal>
          <Reveal delay={150}>
            <div>
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: BRAND }}>
                02 · Now you can customize it to your unique style
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight">
                Congrats!! You have chosen your topic.<br />
                <span style={{ color: BRAND }}>The page is blank no more.</span>
              </h2>
              <p className="mt-5 text-gray-600 text-base md:text-lg leading-relaxed">
                The hardest part of social isn&rsquo;t writing or designing &mdash;
                it&rsquo;s knowing <em>what to post to attract more attention... more eyes on your content</em>. Educate or inspire your audience. Make them laugh. Share a behind-the-scenes story. Post a how-to or a before-and-after.
              </p>
              <ul className="mt-7 space-y-3">
                <BulletRow text="Ideas written for your trade. Plumbers see plumbing. Realtors see realty." />
                <BulletRow text="Crowd-sourced from other businesses in your profession — already proven to engage." />
                <BulletRow text="Run dry? Tap “Generate New Idea” and our AI writes a fresh one in seconds. The well is infinite." />
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* Inline check-bullet primitive used across sections. */
function BulletRow({ text }) {
  return (
    <li className="flex gap-3 items-start">
      <span
        className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white shadow-sm"
        style={{ background: BRAND }}
      >
        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.997 8a1 1 0 01-1.414 0l-3.997-4a1 1 0 011.414-1.42L8 12.58l7.29-7.29a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </span>
      <span className="text-gray-700 text-sm md:text-base leading-relaxed">{text}</span>
    </li>
  );
}

/* Reusable screenshot frame — offset shadow, light ring, optional caption.
 * Includes an automatic single-step src fallback so missing screenshots
 * don't break the page during the redesign rollout. */
function ScreenshotFrame({ src, alt, caption, fallback }) {
  return (
    <figure className="relative">
      <div
        aria-hidden="true"
        className="absolute inset-0 translate-x-3 translate-y-3 rounded-3xl"
        style={{ background: BRAND, opacity: 0.10 }}
      />
      <div className="relative rounded-3xl overflow-hidden bg-white shadow-[0_24px_50px_-20px_rgba(0,0,0,0.18)] ring-1 ring-gray-200/70">
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="block w-full h-auto"
          onError={(e) => {
            if (fallback && !e.currentTarget.dataset.fallback) {
              e.currentTarget.dataset.fallback = '1';
              e.currentTarget.src = fallback;
            }
          }}
        />
      </div>
      {caption && (
        <figcaption className="mt-3 text-center text-xs text-gray-500">{caption}</figcaption>
      )}
    </figure>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * SECTION 4 — FROM IDEA TO 7 PLATFORMS IN 5 MINUTES
 * The compressed Design Center walkthrough. 4 stops, one screenshot each,
 * stacked into a single section. No video — mobile-first.
 * ═══════════════════════════════════════════════════════════════════════════ */
const WALKTHROUGH_STEPS = [
  {
    n: '1',
    title: 'Design',
    desc:  'AI writes the headline, caption, hashtags, and emoji. You tweak. 3.2M stock images and videos at your fingertips, sorted by profession.',
    img:   '/images/home/dc-step1-design.png',
  },
  {
    n: '2',
    title: 'Preview',
    desc:  'See how it lands on Facebook, Instagram, X, LinkedIn, TikTok, YouTube, and WhatsApp — native format for each.',
    img:   '/images/home/dc-step2-preview.png',
  },
  {
    n: '3',
    title: 'Landing page',
    desc:  'Every post links to its own mini-site. AI writes the copy, sections, and CTA. Or point it at your existing URL.',
    img:   '/images/home/dc-step3-landing.png',
  },
  {
    n: '4',
    title: 'Publish',
    desc:  'One click pushes the natively-formatted post to every platform you connected. Track likes, comments, shares, views in one view.',
    img:   '/images/home/dc-step4-publish.png',
  },
];

function FromIdeaToPlatforms() {
  return (
    <section className="bg-white">
      <div className="mx-auto w-[92%] max-w-7xl py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Reveal>
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: BRAND }}>
              03 · Five minutes, start to publish
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight">
              From an idea to <span style={{ color: BRAND }}>seven platforms</span> &mdash; in one sitting.
            </h2>
            <p className="mt-4 text-gray-600 text-base md:text-lg leading-relaxed">
              Four stops. No design tools. No copywriting. No platform-hopping.
            </p>
          </Reveal>
        </div>

        <div className="space-y-16 md:space-y-24">
          {WALKTHROUGH_STEPS.map((step, i) => {
            const reverse = i % 2 === 1; // alternate text-left / text-right
            return (
              <div
                key={step.n}
                className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center ${
                  reverse ? 'md:[&>div:first-child]:order-2' : ''
                }`}
              >
                <Reveal>
                  <div>
                    <div
                      className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white font-bold text-sm mb-4"
                      style={{ background: BRAND }}
                    >
                      {step.n}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-base md:text-lg leading-relaxed max-w-md">
                      {step.desc}
                    </p>
                  </div>
                </Reveal>
                <Reveal delay={150}>
                  <ScreenshotFrame
                    src={step.img}
                    alt={`Design Center walkthrough step ${step.n} — ${step.title}.`}
                    fallback="/images/home/need-inspiration.png"
                  />
                </Reveal>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * SECTION 5 — ANALYTICS IN ONE VIEW
 * Compressed. Two existing screenshots side-by-side. One paragraph.
 * Retains the Meta/TikTok review-friendly platform-name text.
 * ═══════════════════════════════════════════════════════════════════════════ */
function AnalyticsInOneView() {
  return (
    <section className="bg-[#f5fbff] border-y border-[#e0f0fb]">
      <div className="mx-auto w-[92%] max-w-7xl py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <Reveal>
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: BRAND }}>
              04 · See what&rsquo;s working
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight">
              Every platform&rsquo;s numbers in <span style={{ color: BRAND }}>one view</span>.
            </h2>
            <p className="mt-4 text-gray-600 text-base md:text-lg leading-relaxed">
              Once you publish, eHandshake reads engagement back from every connected
              platform &mdash; <strong>likes, comments, shares, and views</strong> &mdash;
              and pins them right next to the post. No more hopping between six dashboards
              for Facebook, Instagram, X, LinkedIn, TikTok, WhatsApp, and YouTube.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
          <Reveal>
            <ScreenshotFrame
              src="/images/team/analytics-dashboardFB.png"
              alt="eHandshake analytics for a Facebook post — likes, comments, shares, and views displayed inline with the creation."
              caption="Facebook engagement, alongside the post."
            />
          </Reveal>
          <Reveal delay={150}>
            <ScreenshotFrame
              src="/images/team/analytics-dashboardTT.png"
              alt="eHandshake analytics for a TikTok post — views, likes, comments, and shares displayed inline with the creation."
              caption="TikTok views, same screen."
            />
          </Reveal>
        </div>

        <p className="mt-10 max-w-2xl mx-auto text-center text-xs md:text-sm text-gray-500 leading-relaxed">
          We only read engagement data you&rsquo;ve authorized for creations you publish
          through eHandshake. Your audience&rsquo;s private information is never accessed or stored.
        </p>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * SECTION 6 — THE TURN ("now multiply it")
 *
 * Bridges from the solo-product story to the team-amplification pitch. The
 * previous design had three text-heavy "philosophy" passages here (Drip
 * Marketing, organic reach essay, "you published, now what?"). Compressed
 * to one visual + two short paragraphs.
 *
 * The dot grid is a single 10×10 (1 of 100) so the math reads instantly. No
 * essays — just the punchline.
 * ═══════════════════════════════════════════════════════════════════════════ */
function DotGrid100({ highlighted = 5 }) {
  return (
    <div className="grid grid-cols-10 gap-[3px] w-fit mx-auto">
      {Array.from({ length: 100 }).map((_, i) => (
        <div
          key={i}
          className="w-2.5 h-2.5 rounded-full"
          style={{ background: i < highlighted ? BRAND : '#e5e7eb' }}
        />
      ))}
    </div>
  );
}

function TheTurn() {
  return (
    <section className="bg-white">
      <div className="mx-auto w-[92%] max-w-5xl py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <Reveal>
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: BRAND }}>
              05 · The reach problem
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight">
              You published. <span style={{ color: BRAND }}>Now who sees it?</span>
            </h2>
          </Reveal>
        </div>

        <Reveal>
          <div className="grid md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center max-w-3xl mx-auto">
            <div className="text-center">
              <DotGrid100 highlighted={5} />
              <p className="mt-4 text-2xl md:text-3xl font-extrabold text-gray-900 leading-none">
                <span style={{ color: BRAND }}>5</span> of every <span style={{ color: BRAND }}>100</span>
              </p>
              <p className="mt-1 text-xs text-gray-500">followers, typical organic reach</p>
            </div>
            <div className="text-gray-700 text-base md:text-lg leading-relaxed space-y-4">
              <p>
                Every platform&rsquo;s algorithm shows posts to the followers most
                likely to engage. For a small-business page, that&rsquo;s about
                5% of your followers per post. Posting more often doesn&rsquo;t
                change the math much.
              </p>
              <p className="font-semibold text-gray-900">
                What does change the math? more voices interacting with the same post.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <p className="mt-14 text-center text-xl md:text-2xl font-semibold tracking-tight" style={{ color: BRAND }}>
            One billboard, one road. Same billboard on five roads &mdash; five times the reach.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * SECTION 7 — YOUR LOCAL TEAM
 *
 * The unique-to-us team amplification pitch. Reuses the existing team
 * dashboard screenshot. Three bullets: build / share / grow.
 *
 * This is the one section where we lean into the BNI-style positioning:
 * non-competing local pros who amplify each other. The product runs the
 * mechanics; the relationships are still human.
 * ═══════════════════════════════════════════════════════════════════════════ */
function YourLocalTeam() {
  return (
    <section className="bg-[#f5fbff] border-y border-[#e0f0fb]">
      <div className="mx-auto w-[92%] max-w-7xl py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <Reveal>
            <div>
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: BRAND }}>
                06 · The unfair advantage
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight">
                Build a team of local pros.<br />
                <span style={{ color: BRAND }}>Multiply every post by five.</span>
              </h2>
              <p className="mt-5 text-gray-600 text-base md:text-lg leading-relaxed">
                Form a small group of <em>non-competing</em> local businesses &mdash;
                one plumber, one realtor, one HVAC tech, one CPA, one insurance
                agent. When any one of you posts, the team shares it. Each post
                rides into five audiences instead of one.
              </p>
              <ul className="mt-7 space-y-3">
                <BulletRow text="Invite the trades that complement yours. Captain your own team or join one in your area." />
                <BulletRow text="Teammates can like, comment, and share each other's posts with one tap — built into the app." />
                <BulletRow text="Your team's combined reviews build a shared reputation. Customers trust groups they recognize." />
                <BulletRow text="The only product that puts BNI-style cross-promotion inside a social publishing tool." />
              </ul>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <ScreenshotFrame
              src="/images/team/team-dashboard.png"
              alt="eHandshake team dashboard — captain, 11 teammates, team score, connected social channels, and an activity feed of recent teammate posts and shares."
              caption="A real team — when anyone posts, it drips through the whole group's combined audience."
            />
          </Reveal>
        </div>

        {/* Mini visual: 5 profession cards in a row, each showing the 5/100
            reach grid. Math punchline below. Kept tight — single row, no
            billboard scene this time (saves vertical space). */}
        <Reveal>
          <div className="mt-14 md:mt-20 max-w-4xl mx-auto">
            <p className="text-center text-sm font-bold uppercase tracking-[0.18em] text-gray-500 mb-6">
              The team math
            </p>
            <div className="grid grid-cols-5 gap-2 md:gap-3">
              {PROFESSIONS.slice(0, 5).map((p) => (
                <div key={p.name} className="bg-white border border-gray-200 rounded-xl p-2.5 md:p-3 text-center">
                  <div className="w-6 h-6 md:w-7 md:h-7 mx-auto mb-1.5" style={{ color: BRAND }}>{p.icon}</div>
                  <p className="text-[10px] md:text-[11px] font-semibold text-gray-700 leading-tight mb-2 min-h-[24px] flex items-center justify-center">
                    {p.name}
                  </p>
                  <div className="grid grid-cols-10 gap-px w-fit mx-auto">
                    {Array.from({ length: 100 }).map((_, i) => (
                      <div
                        key={i}
                        className="w-1 h-1 rounded-full"
                        style={{ background: i < 5 ? BRAND : '#e5e7eb' }}
                      />
                    ))}
                  </div>
                  <p className="mt-1.5 text-[10px] font-bold text-gray-900">
                    <span style={{ color: BRAND }}>5</span>/100
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-7 text-center text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900">
              <span style={{ color: BRAND }}>5</span> from each = <span style={{ color: BRAND }}>25</span> different people
            </p>
            <p className="mt-2 text-center text-sm md:text-base text-gray-600">
              Same post. No extra work. <span className="font-semibold text-gray-900">5× the reach</span> you'd have on your own.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * SECTION 8 — WHY NOW
 *
 * Addresses the "you're new" objection head-on. Two columns: a compact
 * comparison table (lifted spirit from /pricing) + a "new, on purpose"
 * paragraph. No fake user counts — just feature differentiation.
 *
 * Keep this list in sync with PricingPage.jsx's signature rows. If we add
 * a competitor-killing feature there, mirror it here.
 * ═══════════════════════════════════════════════════════════════════════════ */
const ONLY_ON_US = [
  'Team cross-promotion — every teammate amplifies your posts',
  'Profession-tuned post ideas, ready to use',
  'Crowd-sourced ideas from other businesses in your trade',
  'Auto-convert still photos to Reels / TikTok / Shorts video',
  'Built-in landing-page builder with AI-written copy',
  'Branded review-request emails to your customer list',
];

function WhyNow() {
  return (
    <section className="bg-white">
      <div className="mx-auto w-[92%] max-w-7xl py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <Reveal>
            <div>
              <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: BRAND }}>
                07 · Why eHandshake, why now
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight">
                We&rsquo;re new.<br />
                <span style={{ color: BRAND }}>On purpose.</span>
              </h2>
              <p className="mt-5 text-gray-600 text-base md:text-lg leading-relaxed">
                Buffer and Hootsuite were built when &ldquo;social media&rdquo;
                meant a Twitter status update and a Facebook page. They&rsquo;ve
                been bolted-on ever since.
              </p>
              <p className="mt-3 text-gray-600 text-base md:text-lg leading-relaxed">
                eHandshake was built in 2026, for local trades, posting to seven
                platforms, with a team behind them. Profession-aware from line one.
                Team amplification at the core. Nothing else looks like this &mdash;
                because nothing else was built for this.
              </p>
              <div className="mt-7">
                <a
                  href="/pricing"
                  className="inline-flex items-center text-sm font-semibold text-gray-700 hover:text-gray-900 underline underline-offset-4 decoration-[#00aef7] decoration-2"
                >
                  See full feature comparison →
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <div className="rounded-3xl bg-gradient-to-br from-white to-[#f5fbff] border border-[#e0f0fb] p-6 md:p-8 shadow-sm">
              <div className="text-xs font-bold uppercase tracking-[0.18em] mb-4" style={{ color: BRAND }}>
                Only on eHandshake
              </div>
              <ul className="space-y-3">
                {ONLY_ON_US.map((row) => (
                  <li key={row} className="flex gap-3 items-start">
                    <span
                      className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ background: BRAND }}
                      aria-hidden="true"
                    >
                      ✓
                    </span>
                    <span className="text-sm md:text-base text-gray-800 leading-snug">{row}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 pt-5 border-t border-gray-200 text-xs text-gray-500">
                None of Buffer, Hootsuite, Later, SocialBee, or Sprout Social
                ship these. We benchmark every release.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * SECTION 9 — FINAL CTA
 *
 * Light, NOT dark — the whole redesign is about a bright page. Soft cyan
 * background, big CTA, honest "free forever" reassurance.
 * ═══════════════════════════════════════════════════════════════════════════ */
function FinalCTA() {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        background:
          'linear-gradient(135deg, #f5fbff 0%, #e0f4ff 50%, #f5fbff 100%)',
      }}
    >
      {/* Soft brand-color blob, same pattern as the hero — keeps the page's
          top and bottom feeling like bookends. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl"
        style={{ background: 'radial-gradient(circle, #00aef7 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto w-[92%] max-w-4xl py-20 md:py-28 text-center">
        <Reveal>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight">
            Stop staring at the blank page.<br />
            <span style={{ color: BRAND }}>Start showing up.</span>
          </h2>
          <p className="mt-5 text-gray-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Free account in 30 seconds. The AI tags your trade, hands you a
            row of ideas, and walks you through your first post.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href="/?modal=signup"
              className="inline-flex items-center justify-center px-7 py-3.5 rounded-full text-white text-base md:text-lg font-semibold shadow-xl shadow-[#00aef7]/30 transition-transform hover:scale-[1.03]"
              style={{ background: BRAND }}
            >
              Start free →
            </a>
            <a
              href={`${PUBLIC_SITE}/search`}
              className="inline-flex items-center justify-center px-6 py-3.5 rounded-full text-sm md:text-base font-semibold text-gray-700 hover:text-gray-900 border border-gray-300 hover:border-gray-400 transition-colors bg-white/80 backdrop-blur"
            >
              Browse the directory
            </a>
          </div>
          <p className="mt-5 text-xs text-gray-500">
            No card. No trial timer. The free tier is permanent.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * PAGE ROOT
 * ═══════════════════════════════════════════════════════════════════════════ */
const Home = () => {
  return (
    <main className="flex-grow bg-white">
      <LightHero />
      <ProfessionMarquee />
      <TunedToYourTrade />
      <IdeasServedFresh />
      <FromIdeaToPlatforms />
      <AnalyticsInOneView />
      <TheTurn />
      <YourLocalTeam />
      <WhyNow />
      <FinalCTA />

      <ScrollTopButton />

      {/*
        ════════════════════════════════════════════════════════════════════
        SCREENSHOT BRIEF — needed to complete the redesign.
        ════════════════════════════════════════════════════════════════════
        Place all files under /public/images/home/ at the paths shown.
        Aspect ratio for product screenshots: ~16:10 desktop captures.
        PNG preferred; WebP also fine. Width: 1600–2000px for retina.

        1. hero-design-center.png          (LightHero, right column)
           — Design Center mid-flow. Show: the in-app post editor with an
             example plumbing or realtor post being designed. The Inspirations
             strip or the AI caption suggestions panel should be visible.

        2. profession-classified.png       (TunedToYourTrade)
           — Signup wizard's "we detected you're a Plumber" step, OR the
             user profile showing the "Plumber" profession tag prominently.

        3. dc-step1-design.png             (FromIdeaToPlatforms step 1)
           — Design Center: a creation being designed, headline + caption
             + image visible.

        4. dc-step2-preview.png            (step 2)
           — Multi-platform preview tabs visible (FB / IG / X / LI / TT / YT / WA).

        5. dc-step3-landing.png            (step 3)
           — Landing-page builder view with section blocks and CTA.

        6. dc-step4-publish.png            (step 4)
           — Sharing Center showing the cross-platform publish view.

        Existing reusable assets (no new capture needed):
           - /images/home/need-inspiration.png
           - /images/team/analytics-dashboardFB.png
           - /images/team/analytics-dashboardTT.png
           - /images/team/team-dashboard.png

        Fallback behavior:
        Each <ScreenshotFrame> has onError → falls back to need-inspiration.png
        so the page renders even before captures land. Replace as you go.
        ════════════════════════════════════════════════════════════════════
      */}
    </main>
  );
};

export default Home;
